<!-- packages/studio/src/components/army/UpgradeGroupForm.vue -->
<template>
  <div class="upgrade-group-form">
    <!-- Hidden when an ancestor (GroupEditor) already shows the id in its header. -->
    <StIdField
      v-if="!hideId"
      :model-value="modelValue.id"
      prefix="upgrade"
      @update:model-value="patch({ id: $event })"
    />
    <StField v-if="!hideName" label="Name" hint="Matches the canonical group when patching">
      <StTextInput
        :model-value="modelValue.name"
        @update:model-value="patch({ name: $event ?? '' })"
      />
    </StField>

    <section class="form-section">
      <div class="st-eyebrow">{{ singleAbility ? 'Ability' : 'Abilities' }}</div>
      <AbilityForm
        :model-value="modelValue.abilities"
        :single="singleAbility"
        :forced-type="forcedAbilityType"
        :forced-keyword="forcedKeyword"
        @update:model-value="patch({ abilities: $event })"
      />
    </section>

    <section
      v-if="!hideRules"
      class="form-section"
      :class="{ 'upgrade-group-form__rules--top': rulesPosition === 'top' }"
    >
      <div class="st-eyebrow">Rules</div>
      <EditableList
        :items="modelValue.rules"
        add-label="Add rule"
        remove-label="Remove rule"
        @add="addRule"
        @remove="removeRule"
      >
        <template #row="{ index }">
          <StField label="Name">
            <StTextInput
              :model-value="modelValue.rules[index].name"
              @update:model-value="patchRule(index, { name: $event ?? '' })"
            />
          </StField>
          <StField label="Description">
            <StTextarea
              :model-value="modelValue.rules[index].description"
              @update:model-value="patchRule(index, { description: $event ?? '' })"
            />
          </StField>
        </template>
      </EditableList>
    </section>

    <StField
      v-if="showPoints"
      class="upgrade-group-form__points"
      :class="{ 'upgrade-group-form__points--top': pointsPosition === 'top' }"
      label="Points"
      hint="0 if free"
    >
      <StNumberInput
        :model-value="modelValue.points"
        :min="0"
        @update:model-value="patch({ points: $event ?? 0 })"
      />
    </StField>
  </div>
</template>

<script setup lang="ts">
import type { IUpgrade, IRule } from '@scrolldex/shared';
import AbilityForm from '../AbilityForm.vue';
import EditableList from '../EditableList.vue';
import { StField, StTextInput, StTextarea, StNumberInput, StIdField } from '../ui/index.js';

const props = defineProps<{
  modelValue: IUpgrade;
  hideName?: boolean;
  // Suppress the id field (an ancestor shows it in a header instead).
  hideId?: boolean;
  // Single-ability mode (battle formations have exactly one ability)…
  singleAbility?: boolean;
  // …and no rules section.
  hideRules?: boolean;
  // Show a point-cost field (enhancements and lores can cost points).
  showPoints?: boolean;
  // Where the Points field sits, matching where the cost renders: 'top' for a
  // lore (chip by the name), 'bottom' for an enhancement (in the ability footer).
  pointsPosition?: 'top' | 'bottom';
  // Where the Rules section sits ('top' for battle traits).
  rulesPosition?: 'top' | 'bottom';
  // Force the ability type / inject a keyword (lores — see AbilityForm).
  forcedAbilityType?: number;
  forcedKeyword?: string;
}>();
const emit = defineEmits<{ (e: 'update:modelValue', value: IUpgrade): void }>();

function patch(part: Partial<IUpgrade>) {
  emit('update:modelValue', { ...props.modelValue, ...part });
}
function patchRule(index: number, part: Partial<IRule>) {
  patch({ rules: props.modelValue.rules.map((r, i) => (i === index ? { ...r, ...part } : r)) });
}
function addRule() {
  patch({ rules: [...props.modelValue.rules, { name: '', description: '' }] });
}
function removeRule(index: number) {
  patch({ rules: props.modelValue.rules.filter((_, i) => i !== index) });
}
</script>

<style scoped>
.upgrade-group-form {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-4);
}
.upgrade-group-form__points {
  max-width: 8rem;
}
/* Render the Points field first when it should sit at the top of the form. */
.upgrade-group-form__points--top {
  order: -1;
}
/* Render the Rules section first (battle traits show rules above abilities). */
.upgrade-group-form__rules--top {
  order: -1;
}
.form-section {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}
</style>
